import type { Tracer } from 'dd-trace';
import { isClient, isIE } from '@mop/shared/utils/util';
import cookie from '@mop/shared/utils/cookie';
let tracer: Tracer | null | undefined;

export const isLocalhostOrIsDebugCookieEnabled: boolean =
  isClient && (window.location.host.includes('localhost') || Boolean(cookie().get('debug')));

export function logError(error: any) {
  if (isIE()) {
    return;
  }
  // prevent reporting request cancelation,
  // but report in console
  const errorMessage = String(error?.message ?? '').toLowerCase();
  if (
    error?.name === 'AbortError' ||
    errorMessage.includes('abort') ||
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('importing a module script failed') ||
    errorMessage.includes('error loading dynamically imported module') ||
    errorMessage.includes('load failed')
  ) {
    if (!isLocalhostOrIsDebugCookieEnabled) {
      return;
    }
  }
  if (isClient) {
    window.mopLogger?.logError && window.mopLogger.logError(error);
  } else {
    getTracer().then((tracer) => {
      if (!tracer) {
        return;
      }
      const span = tracer.scope().active();
      if (span) {
        span.setTag('error', toError(error));
      }
    });
  }

  if (console) {
    console.error(error); // eslint-disable-line
  }
}

export function logWarning(warning: any) {
  if (isIE()) {
    return;
  }
  if (isClient) {
    window.mopLogger?.logWarning && window.mopLogger.logWarning(warning);

    if (typeof console === 'undefined' || !console || !isLocalhostOrIsDebugCookieEnabled) {
      return;
    }
    console.warn(warning); // eslint-disable-line
  } else {
    console.warn(warning); // eslint-disable-line
  }
}

export function logInfo(info: any) {
  if (isIE()) {
    return;
  }
  if (isClient) {
    if (typeof console === 'undefined' || !console || !isLocalhostOrIsDebugCookieEnabled) {
      return;
    }
    console.info(info); // eslint-disable-line
  } else {
    console.info(info); // eslint-disable-line
  }
}

function toError(input: unknown): Error {
  if (input instanceof Error) return input;

  if (typeof input === 'string') {
    return new Error(input);
  }

  try {
    return new Error(JSON.stringify(input));
  } catch {
    return new Error(String(input));
  }
}

async function getTracer() {
  if (tracer !== undefined) {
    return tracer;
  }

  // @ts-ignore
  if (typeof process === 'undefined') {
    tracer = null;
    return tracer;
  }

  try {
    tracer = (await import('dd-trace')).default;
    return tracer;
  } catch {
    tracer = null;
    return tracer;
  }
}
